import { Loader2, Pause, Speech } from "lucide-react";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import { toast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";

interface TTSButtonProps {
  text: string;
  isLoading?: boolean;
  rate?: number;
  className?: string;
  size?: "sm" | "mini";
  disabled?: boolean;
  labelId?: string;
  color?: string;
  onBoundary?: (e: { charIndex: number; charLength: number }) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

const DEFAULT_RATE = 1.25;

export function TTSButton({
  text,
  isLoading = false,
  rate = DEFAULT_RATE,
  className,
  size = "mini",
  disabled = false,
  labelId,
  color = "foreground",
  onBoundary,
  onStart,
  onEnd,
}: TTSButtonProps) {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isSynthesizing, setIsSynthesizing] = React.useState(false);
  const [voices, setVoices] = React.useState<SpeechSynthesisVoice[]>([]);
  const speechSynthesisRef = React.useRef<SpeechSynthesis | null>(null);
  const utteranceRef = React.useRef<SpeechSynthesisUtterance | null>(null);

  const hasSynth = typeof window !== "undefined" && "speechSynthesis" in window;

  const updateVoices = React.useCallback(() => {
    if (!hasSynth) {
      setVoices([]);
      return;
    }
    const v = window.speechSynthesis.getVoices?.() ?? [];
    setVoices(v);
  }, [hasSynth]);

  React.useEffect(() => {
    if (!hasSynth) return;
    speechSynthesisRef.current = window.speechSynthesis;
    updateVoices();

    let tries = 0;
    const iv = setInterval(() => {
      const v = window.speechSynthesis.getVoices?.() ?? [];
      if (v.length > 0 || tries++ > 20) {
        if (v.length > 0) setVoices(v);
        clearInterval(iv);
      }
    }, 250);

    if ("onvoiceschanged" in window.speechSynthesis) {
      const handler = () => updateVoices();
      window.speechSynthesis.onvoiceschanged = handler;
      return () => {
        if (window.speechSynthesis.onvoiceschanged === handler) {
          window.speechSynthesis.onvoiceschanged = null as any;
        }
        clearInterval(iv);
        speechSynthesisRef.current?.cancel();
      };
    }

    return () => {
      clearInterval(iv);
      speechSynthesisRef.current?.cancel();
    };
  }, [hasSynth, updateVoices]);

  const handleClick = () => {
    if (!speechSynthesisRef.current || !text) return;

    if (voices.length === 0) {
      toast({
        title: "Text-to-Speech Unavailable",
        description:
          "TTS voices are still loading. Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    if (isPlaying) {
      speechSynthesisRef.current.cancel();
      setIsPlaying(false);
      return;
    }

    if (speechSynthesisRef.current.paused && utteranceRef.current) {
      speechSynthesisRef.current.resume();
      setIsPlaying(true);
      return;
    }

    setIsSynthesizing(true);
    const utterance = new SpeechSynthesisUtterance(text);
    const preferred =
      voices.find((v) => v.lang?.toLowerCase().startsWith("en")) ?? voices[0];
    if (preferred) utterance.voice = preferred;
    utterance.rate = rate;
    utterance.lang = preferred?.lang ?? "en-US";

    utterance.onstart = () => {
      setIsPlaying(true);
      setIsSynthesizing(false);
      onStart?.();
    };

    utterance.onend = () => {
      setIsPlaying(false);
      utteranceRef.current = null;
      onEnd?.();
    };

    utterance.onerror = (event) => {
      setIsPlaying(false);
      setIsSynthesizing(false);
      if (event.error === "interrupted" || event.error === "canceled") {
        return;
      }
      toast({
        title: "TTS Error",
        description: `An error occurred during playback: ${event.error}`,
        variant: "destructive",
      });
    };

    utterance.onboundary = (event: SpeechSynthesisEvent) => {
      const anyEvent = event as unknown as {
        name?: string;
        charIndex?: number;
        charLength?: number;
      };
      const hasWordInfo =
        anyEvent.name === "word" || typeof anyEvent.charLength === "number";
      if (hasWordInfo && onBoundary) {
        onBoundary({
          charIndex:
            typeof anyEvent.charIndex === "number" ? anyEvent.charIndex : 0,
          charLength:
            typeof anyEvent.charLength === "number" ? anyEvent.charLength : 0,
        });
      }
    };

    utteranceRef.current = utterance;
    speechSynthesisRef.current.speak(utterance);
  };

  const isDisabled = isLoading || disabled || !text;

  if (!hasSynth || voices.length === 0 || !text?.trim()) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size={size}
      disabled={isDisabled}
      onClick={handleClick}
      className={cn(
        `inline-flex items-center text-${color} hover:text-${color}`,
        size === "mini" ? "px-0 text-xs hover:bg-transparent" : "",
        className
      )}
      aria-label={isPlaying ? "Stop" : "Read"}
    >
      {isSynthesizing ? (
        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
      ) : isPlaying ? (
        <Pause className="w-4 h-4 mr-1" />
      ) : (
        <Speech className="w-4 h-4 mr-1" />
      )}
      <FormattedMessage
        id={isPlaying ? "common.stop" : (labelId ?? "common.read")}
        defaultMessage={isPlaying ? "Stop" : "Read"}
      />
    </Button>
  );
}
